import type { ProductPriceDetailDto, ProductPricePurchaseType } from '@/types/api/product';
import { formatCurrencyOrDash, formatCurrency } from './currencyFormat';

/** 판매단위 목록: 통화에 맞게 금액 표시 (0 이하·미입력은 '-') */
export function formatSalesUnitMoneyCell(amount: number | null | undefined, currency: string | undefined): string {
    return formatCurrencyOrDash(amount, currency);
}

/**
 * 관리자(국내) 화면: 판매단위 금액을 원화 표기로만 표시.
 */
export function formatSalesUnitMoneyAdminKrw(amount: number | null | undefined): string {
    return formatCurrencyOrDash(amount, 'KRW');
}

export { formatCurrency };

/** options.unit → API와 동일한 구매유형 표기 */
export function displayPurchaseTypeFromUnit(unit: string | undefined): ProductPricePurchaseType {
    const map: Record<string, ProductPricePurchaseType> = {
        per_user: 'PER_USER',
        per_device: 'PER_DEVICE',
        per_license: 'PER_LICENSE',
        per_item: 'PER_ITEM',
    };
    return map[unit ?? 'per_user'] ?? 'PER_USER';
}

/**
 * API에서 받은 purchaseType을 제품 타입에 맞게 보정하여 표시.
 * 기존에 잘못 저장된 PER_USER 데이터를 제품 타입 기반으로 올바르게 보여줌.
 */
/**
 * 제품 타입에 따라 올바른 구매유형을 반환.
 * 제품 타입이 장비/커스텀 서비스면 타입을 우선 적용하여 기존 잘못 저장된 값도 덮어씀.
 */
export function resolvePurchaseTypeDisplay(
    purchaseType: string | undefined,
    productType: string | undefined
): ProductPricePurchaseType {
    if (purchaseType === 'NONE') return 'NONE';

    const pt = (productType ?? '').toUpperCase().replace(/-/g, '_');

    // 제품 타입이 장비면 항상 PER_ITEM (레거시 행; 신규는 API `NONE`)
    if (pt === 'EQUIPMENT' || pt.includes('EQUIPMENT')) return 'PER_ITEM';

    // 제품 타입이 커스텀 서비스면 항상 PER_LICENSE (레거시 행; 신규는 API `NONE`)
    if (pt === 'CUSTOM_SERVICE' || pt === 'CUSTOMSERVICE') return 'PER_LICENSE';

    // 콘텐츠류: API에서 받은 값 그대로 사용
    return (purchaseType as ProductPricePurchaseType) ?? 'PER_USER';
}

function trimmedOrEmpty(s: unknown): string {
    if (s == null) return '';
    const x = String(s).trim();
    return x;
}

function coercePositiveCountryId(v: unknown): number | undefined {
    if (v == null) return undefined;
    const n = typeof v === 'number' && Number.isFinite(v) ? v : Number(String(v).trim());
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return Math.floor(n);
}

/**
 * GET /product 상세 `prices[]` 행의 판매지역 표시 — 단일 국가·countries 배열·글로벌.
 */
export function formatSalesUnitRegionDisplay(
    price: Pick<ProductPriceDetailDto, 'countryTitle' | 'countryId'>,
    countryIdToTitle: Map<number, string>,
    globalLabel: string
): string {
    const direct = trimmedOrEmpty(price.countryTitle);
    if (direct) return direct;

    const id = coercePositiveCountryId(price.countryId);
    if (id != null) return countryIdToTitle.get(id) ?? `ID ${id}`;

    return globalLabel;
}
